import { Box, Divider, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { getDisplayNameForRole } from '@verantos-dev/core-auth';
import { UserRolesIdsEnum } from '@verantos-dev/core-enums';
import { useGetOrganizationQuery } from '@verantos-dev/data-access-organizations';
import { UserInfo } from '@verantos-dev/data-access-users';
import { StatusDisplay } from '@verantos-dev/shared-components';
import { formatDate } from '@verantos-dev/shared-utils';

import { DialogActionType, useUsersListDialogContext } from '../context/UsersListDialogContext';

export const useUsersListInfoDialog = (userData: UserInfo) => {
  const intl = useIntl();
  const theme = useTheme();

  const { dispatch } = useUsersListDialogContext();
  const { data } = useGetOrganizationQuery({
    organizationId: userData.organizationId,
  });

  // fetch strings
  const dialogHeader = intl.formatMessage({
    id: 'users.userInfoDialog.dialogHeader',
    defaultMessage: 'User details',
  });
  const title = intl.formatMessage({
    id: 'users.userInfoDialog.title',
    defaultMessage: 'User details',
  });
  const logHeader = intl.formatMessage({
    id: 'users.userInfoDialog.logHeader',
    defaultMessage: 'Log details',
  });

  // value labels - user details
  const fullName = intl.formatMessage({
    id: 'users.userInfoDialog.fullName',
    defaultMessage: 'Full name',
  });
  const status = intl.formatMessage({
    id: 'users.userInfoDialog.status',
    defaultMessage: 'Status',
  });
  const emailAddress = intl.formatMessage({
    id: 'users.userInfoDialog.emailAddress',
    defaultMessage: 'Email address',
  });
  const roles = intl.formatMessage({ id: 'users.userInfoDialog.roles', defaultMessage: 'Roles' });
  const organization = intl.formatMessage({
    id: 'users.userInfoDialog.organization',
    defaultMessage: 'Organization',
  });

  // value labels - log details
  const lastLogin = intl.formatMessage({
    id: 'users.userInfoDialog.lastLogin',
    defaultMessage: 'Last login',
  });
  const createdBy = intl.formatMessage({
    id: 'users.userInfoDialog.createdBy',
    defaultMessage: 'Created by',
  });
  const createdOn = intl.formatMessage({
    id: 'users.userInfoDialog.createdOn',
    defaultMessage: 'Created on',
  });
  const lastModifiedBy = intl.formatMessage({
    id: 'users.userInfoDialog.lastModifiedBy',
    defaultMessage: 'Last modified by',
  });
  const lastModifiedOn = intl.formatMessage({
    id: 'users.userInfoDialog.lastModifiedOn',
    defaultMessage: 'Last modified on',
  });

  const convertRolesToDisplay = () => {
    const roles = userData.roles.map((role) =>
      getDisplayNameForRole(role as UserRolesIdsEnum, intl)
    );
    return roles.join(', ');
  };

  const textSection = (label: string, value: string) => {
    return (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: theme.spacing(4) }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color={theme.palette.text.primary}>
              {label}
            </Typography>
          </Box>
          <Box sx={{ flex: 2 }}>
            <Typography variant="body1" color={theme.palette.text.primary} fontWeight={500}>
              {value}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ my: theme.spacing(3) }} />
      </>
    );
  };

  const organizationName = data?.data.displayName || userData.organizationName;

  const userDetails = () => {
    return (
      <>
        {textSection(fullName, `${userData.firstName} ${userData.lastName}`)}

        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Box sx={{ flex: 1, mr: theme.spacing(4) }}>
            <Typography variant="body2">{status}</Typography>
          </Box>
          <Box sx={{ flex: 2 }}>
            <StatusDisplay
              id={`users-info-status-${userData.userId}`}
              status={userData.userStatus}
            />
          </Box>
        </Box>
        <Divider sx={{ my: theme.spacing(3) }} />

        {textSection(emailAddress, userData.email)}

        {textSection(roles, convertRolesToDisplay())}

        {textSection(organization, organizationName)}
      </>
    );
  };

  const logDetails = () => {
    return (
      <>
        {/*  TODO Art - update whem V2 users api is ready \ */}
        {textSection(
          lastLogin,
          userData.modifiedBy?.transactionDate
            ? formatDate(userData.modifiedBy?.transactionDate?.toString(), 'dateShort')
            : '--'
        )}
        {textSection(
          createdBy,
          `${userData?.createdBy?.firstName} ${userData?.createdBy?.lastName}`
        )}
        {textSection(
          createdOn,
          userData?.createdBy?.transactionDate
            ? formatDate(userData?.createdBy?.transactionDate?.toString(), 'dateShort')
            : '--'
        )}
        {textSection(
          lastModifiedBy,
          `${userData.modifiedBy?.firstName} ${userData.modifiedBy?.lastName}`
        )}
        {textSection(
          lastModifiedOn,
          userData?.modifiedBy?.transactionDate
            ? formatDate(userData?.modifiedBy?.transactionDate.toString(), 'dateShort')
            : '--'
        )}
      </>
    );
  };

  const onClose = () => {
    dispatch({ type: DialogActionType.CLOSE_DIALOG });
  };

  return {
    dialogHeader,
    title,
    logHeader,

    userDetails,
    logDetails,

    onClose,
  };
};
